/*
RingBuffer: A Justification
For write-heavy, latency-sensitive tasks (say, if you want to log the most recent N satchel events
as satchel middleware which executes synchronously...), we might actually start to care about
javascript array method performance.

RingBuffer profiled against Array bounded insert via push/splice and unshift/splice (total times in ms):
profile(i => ringBuffer.append(i), 1000000)
105

profile(i => pushWithBound(i, pushBuffer, 200), 1000000)
392

profile(i => unshiftWithBound(i, unshiftBuffer, 200), 1000000)
494

RingBuffer performs, on average, roughly 4-5x faster
*/

/**
 * A fixed-length array optimized for quick bounded insert
 */
export class RingBuffer<T> implements Iterable<T> {
    private start: number;
    private length: number;
    private buffer: Array<T>;

    constructor(length: number) {
        this.buffer = new Array<T>(length);
        this.start = 0;
        this.length = 0;
    }

    /**
     * Iterate over the ring buffer starting from the oldest item in the buffer
     */
    public [Symbol.iterator](): Iterator<T> {
        const ringBuffer = this;
        let i = 0;

        return {
            next() {
                return {
                    done: i >= ringBuffer.size,
                    value: ringBuffer.get(i++),
                };
            },
        };
    }

    /**
     * Converts an offset from the oldest item in the ringbuffer to an index in the internal buffer
     */
    private index(offset: number): number {
        return (this.start + offset) % this.buffer.length;
    }

    /**
     * Get the item in the buffer at the specified index
     * If the specified index is greater than the number of currently appended elements
     * or the total size of the specified buffer length, this will return undefined.
     * @param index the offset from the start (oldest item) of the ring buffer
     */
    public get(index: number): T {
        return this.buffer[this.index(index)];
    }

    /**
     * Append to the ring buffer, overwriting the oldest item in the ring if necessary
     * @param item the item to append
     */
    public append(item: T) {
        this.buffer[this.index(this.length)] = item;
        if (this.length === this.buffer.length) {
            this.start = (this.start + 1) % this.buffer.length;
        } else {
            this.length += 1;
        }
    }

    /**
     * The number of items stored in the ring buffer
     */
    public get size(): number {
        return this.length;
    }
}
